import { useMemo } from 'react';
import sortBy from 'lodash.sortby';
import type { SWRConfiguration } from 'swr';

import { useApi, useApiWithDataById, usePostSWRFetcher } from 'data/base-api';
import type {
  DetailedProject,
  Project,
  ProjectDetails,
  ProjectTask,
  Task,
} from 'model/Projects/types';
import { filterAndSortTasks } from 'model/Projects/utils';

const baseUrl = '/api/v3/project';

export const useProjectTaskTree = (
  projId: string | undefined,
  revalidateOnMount = false
) => {
  const { data, ...rest } = useApi<ProjectTask[]>(
    projId ? `${baseUrl}/tasks/${projId}/tree` : null,
    {
      revalidateOnMount,
    }
  );

  const tasks = useMemo(() => {
    if (data) {
      return filterAndSortTasks(data);
    }

    return undefined;
  }, [data]);

  return {
    tasks,
    ...rest,
  };
};

export const useGetTasks = (projectId: string | null) =>
  useApi<Task[]>(projectId ? `${baseUrl}/tasks/${projectId}` : null);

export const useProjects = (
  companyId: string | undefined,
  config?: SWRConfiguration
) => {
  const { data, ...rest } = useApiWithDataById<Project[], Project>(
    companyId ? `${baseUrl}/${companyId}` : null,
    config
  );

  const resultData = useMemo(() => {
    if (!data) return [];
    return sortBy(data, 'name');
  }, [data]);

  return {
    data: resultData,
    rawData: data,
    ...rest,
  };
};

export const useGetProjectsWithCompanies = (companyIds: string[]) => {
  const { data, ...rest } = usePostSWRFetcher(
    companyIds ? `${baseUrl}/company-ids/0` : null,
    companyIds
  );

  return {
    data,
    ...rest,
  };
};

export const useGetAllProjects = (
  activeOnly = true,
  userProjectsOnly = true,
  skip = false
) => {
  const params = new URLSearchParams();
  params.set('activeOnly', activeOnly.toString());
  params.set('userProjectsOnly', userProjectsOnly.toString());

  const { data, ...rest } = useApiWithDataById<
    DetailedProject[],
    DetailedProject
  >(skip ? null : `/api/projects/lookuplist/all?${params}`);

  return {
    data,
    ...rest,
  };
};

export const useGetCompanyProjects = () =>
  useApi<DetailedProject[]>('/api/projects');

export const useGetProject = (id: string | undefined) => {
  const { data, ...rest } = useApi<DetailedProject>(
    id ? `/api/projects/${id}` : null
  );
  return {
    project: data,
    ...rest,
  };
};

export const useGetProjectDetails = (
  id: string | undefined,
  config?: SWRConfiguration
) =>
  useApi<ProjectDetails>(
    id ? `${baseUrl}/project-details/${id}` : null,
    config
  );
